import Fuse from 'fuse.js'
import type { ComponentType } from '@/types/schemas/workspace'
import { COMPONENT_META, PANEL_CATEGORY } from './add-panel-menu-constants'

/**
 * Words people reach for that are not in a panel's title: the chart form
 * ("pie", "sankey", "line"), the data it shows ("earnings", "headlines"), or
 * what it answers ("time of day"). The Add Panel search matches these as well as the
 * title and category, so a panel is found by what it IS, not only by its name.
 * Every dashboard and History panel has an entry (drift-guarded in the test).
 */
export const PANEL_KEYWORDS: Partial<Record<ComponentType, string[]>> = {
  // ── Dashboard ──
  chart: ['candles', 'candlestick', 'price', 'ohlc', 'ticker'],
  scanner: ['screener', 'movers', 'gappers', 'runners', 'scan'],
  watchlist: ['list', 'favorites', 'tickers', 'symbols'],
  news: ['headlines', 'press release', 'sec', 'filings', 'catalyst'],
  'key-stats': ['fundamentals', 'float', 'market cap', 'short interest'],
  'ticker-stats': ['gap stats', 'day stats', 'profile', 'history'],
  'multi-chart': ['grid', 'charts', 'multiple'],
  'multi-scanner': ['scanners', 'tabs', 'multiple', 'screener'],
  'market-overwatch': ['indices', 'market', 'spy', 'qqq', 'breadth'],
  'sector-heatmap': ['sectors', 'treemap', 'heat'],
  'theme-tracker': ['themes', 'groups', 'sympathy', 'sector'],
  calendar: ['earnings', 'economic', 'events', 'ipo', 'splits'],
  notebook: ['notes', 'journal', 'research', 'write'],
  'alert-windows': ['alerts', 'window', 'matches', 'profile', 'live', 'signal'],
  'price-alerts': ['alerts', 'alarm', 'notify', 'level', 'trigger', 'price'],
  'scanner-parity': ['parity', 'compare', 'real time', 'database', 'diff', 'dev tool'],
  // ── History ──
  'history-records': ['table', 'grid', 'rows', 'list', 'data'],
  'history-chart': ['candles', 'candlestick', 'price', 'ohlc'],
  'history-multi-chart': ['grid', 'charts', 'multiple'],
  'history-stats': ['summary', 'averages', 'win rate', 'metrics'],
  'history-news': ['headlines', 'press release', 'catalyst'],
  'history-comparison': ['compare', 'periods', 'year over year', 'versus'],
  'history-time-series': ['line', 'trend', 'over time', 'area'],
  'history-scatter': ['correlation', 'dots', 'relationship', 'x y'],
  'history-bar': ['column', 'by date', 'weekly', 'monthly'],
  'history-bar-category': ['column', 'sector', 'group', 'breakdown'],
  'history-donut-category': ['pie', 'share', 'breakdown', 'sector'],
  'history-intraday': ['intraday', 'minute', 'price action', 'overlay'],
  'history-calendar': ['days', 'month grid', 'heat'],
  'history-sankey': ['sankey', 'flow', 'funnel', 'outcomes'],
  'history-scorecard': ['kpi', 'score', 'tiles', 'summary'],
  'history-heatmap': ['matrix', 'pivot', 'heat'],
  'history-time-distribution': ['time of day', 'timing', 'hour', 'when'],
  'tags-explorer': ['tags', 'labels', 'tagged'],
}

interface PanelSearchEntry {
  type: ComponentType
  label: string
  category: string
  keywords: string[]
}

// Entries and the fuzzy index per offered-types list (the dashboard and History
// lists are module constants), built on first search rather than per keystroke.
const entryCache = new WeakMap<readonly ComponentType[], PanelSearchEntry[]>()
const indexCache = new WeakMap<readonly ComponentType[], Fuse<PanelSearchEntry>>()

function entriesFor(types: readonly ComponentType[]): PanelSearchEntry[] {
  let entries = entryCache.get(types)
  if (!entries) {
    entries = types.map((type) => ({
      type,
      label: COMPONENT_META[type].label,
      category: PANEL_CATEGORY[type] ?? '',
      keywords: PANEL_KEYWORDS[type] ?? [],
    }))
    entryCache.set(types, entries)
  }
  return entries
}

function indexFor(types: readonly ComponentType[]): Fuse<PanelSearchEntry> {
  let fuse = indexCache.get(types)
  if (!fuse) {
    fuse = new Fuse(entriesFor(types), {
      // The title outranks a search word, which outranks the group name.
      keys: [
        { name: 'label', weight: 3 },
        { name: 'keywords', weight: 1.5 },
        { name: 'category', weight: 0.5 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
    })
    indexCache.set(types, fuse)
  }
  return fuse
}

function startsAWord(field: string, q: string): boolean {
  const f = field.toLowerCase()
  return f.startsWith(q) || f.includes(` ${q}`) || f.includes(`-${q}`)
}

/**
 * How well an entry matches by word prefix, lower is better; null for no match.
 * Title start, then a later title word, then a search word, then the group.
 */
function prefixRank(entry: PanelSearchEntry, q: string): number | null {
  if (entry.label.toLowerCase().startsWith(q)) return 0
  if (startsAWord(entry.label, q)) return 1
  if (entry.keywords.some((k) => startsAWord(k, q))) return 2
  if (entry.category && startsAWord(entry.category, q)) return 3
  return null
}

/**
 * The offered panel types matching `query`, best match first; null for a blank
 * query (the hub shows its full grid then) and [] when nothing matches.
 *
 * Word-prefix matching comes first: it keeps a three-letter query to the
 * panels it plainly names, where fuzzy scoring alone would light up half the
 * grid on scattered letters. Fuzzy matching is only the fallback for a typo
 * ("scater"), when no word starts with the query at all.
 */
export function searchPanelTypes(
  query: string,
  types: readonly ComponentType[],
): ComponentType[] | null {
  const q = query.trim().toLowerCase()
  if (!q) return null
  const ranked = entriesFor(types)
    .map((entry, order) => ({ entry, order, rank: prefixRank(entry, q) }))
    .filter((r): r is { entry: PanelSearchEntry; order: number; rank: number } => r.rank != null)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
  if (ranked.length > 0) return ranked.map((r) => r.entry.type)
  return indexFor(types)
    .search(q)
    .map((r) => r.item.type)
}
